/**
 * useCacheSync - Centralized cache synchronization hook
 *
 * Provides methods for:
 * - Instant grid row updates via apiRef
 * - React Query cache updates (primary and server-side search caches)
 * - Cache invalidation for full refetch
 */

import { useCallback } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { GridApiPremium } from '@mui/x-data-grid-premium'
import { EntityConfig } from '../types/EntityConfig'

interface RowUpdate {
  id: string | number
  rowId?: string | number
  [key: string]: any
}

interface BatchUpdatePayload {
  PK: string
  SK: string
  fieldName: string
  newValue: any
}

export const useCacheSync = <TRow extends Record<string, any>>(config: EntityConfig<TRow>) => {
  /**
   * Directly update grid rows via apiRef (instant UI feedback)
   * This bypasses React's re-render cycle for immediate visual updates
   */
  const updateGridRowsDirectly = useCallback(
    (apiRef: React.RefObject<GridApiPremium>, rowUpdates: RowUpdate[]) => {
      if (!apiRef?.current?.updateRows) {
        console.warn('[useCacheSync] No apiRef.updateRows available, skipping direct grid update')
        return
      }

      try {
        apiRef.current.updateRows(rowUpdates)
      } catch (error) {
        console.error('[useCacheSync] Failed to update grid rows directly:', error)
      }
    },
    []
  )

  /**
   * Update React Query caches
   *
   * @param queryClient - React Query client
   * @param updates - Batch update payloads (contains PK, SK for matching)
   * @param normalizedFields - Field values to apply to matched rows
   */
  const updateCache = useCallback(
    (
      queryClient: QueryClient,
      updates: BatchUpdatePayload[],
      normalizedFields: Record<string, any>
    ) => {
      // Build lookup map for efficient matching
      const updatesByKey = new Map<string, Record<string, any>>()
      updates.forEach(u => {
        if (u.PK && u.SK) {
          updatesByKey.set(`${u.PK}::${u.SK}`, normalizedFields)
        }
      })

      // Helper to find matching entity
      const findUpdate = (item: any): Record<string, any> | undefined => {
        // Try composite key first
        if (item.PK && item.SK) {
          const match = updatesByKey.get(`${item.PK}::${item.SK}`)
          if (match) return match
        }

        // Try by primary ID field
        for (const [key, fields] of updatesByKey.entries()) {
          const [pk, sk] = key.split('::')
          if (item[config.cache.primaryIdField] === pk) return fields
          if (config.cache.secondaryIdField && item[config.cache.secondaryIdField] === pk) return fields
        }

        return undefined
      }

      // 1. Update primary cache (e.g., SEARCH_DATA_KEY or test cache)
      queryClient.setQueryData(config.cache.primaryKey, (oldData: any) => {
        if (!oldData) return oldData

        // Handle different cache structures
        if (Array.isArray(oldData)) {
          return oldData.map((item: any) => {
            const updates = findUpdate(item)
            return updates ? { ...item, ...updates } : item
          })
        }

        if (oldData[config.cache.dataKey]) {
          return {
            ...oldData,
            [config.cache.dataKey]: oldData[config.cache.dataKey].map((item: any) => {
              const updates = findUpdate(item)
              return updates ? { ...item, ...updates } : item
            }),
          }
        }

        return oldData
      })

      // 2. Update all matching server-side search queries
      if (config.cache.searchKeyPrefixes.length > 0) {
        queryClient.setQueriesData(
          {
            predicate: q =>
              Array.isArray(q.queryKey) &&
              q.queryKey[0] === 'search' &&
              config.cache.searchKeyPrefixes.includes(q.queryKey[1] as string),
          },
          (oldData: any) => {
            if (!oldData?.rows) return oldData

            const newRows = oldData.rows.map((row: any) => {
              const updates = findUpdate(row)
              return updates ? { ...row, ...updates } : row
            })

            return {
              ...oldData,
              rows: newRows,
            }
          }
        )
      }
    },
    [config]
  )

  /**
   * Invalidate caches to force refetch
   */
  const invalidateCache = useCallback(
    (queryClient: QueryClient) => {
      // Invalidate primary cache
      queryClient.invalidateQueries({ queryKey: config.cache.primaryKey })

      // Invalidate server-side search caches
      config.cache.searchKeyPrefixes.forEach(prefix => {
        queryClient.invalidateQueries({
          predicate: q =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'search' &&
            q.queryKey[1] === prefix,
        })
      })
    },
    [config]
  )

  /**
   * Apply updates to local state (for mock/prototype mode)
   */
  const applyUpdatesToRows = useCallback(
    (rows: TRow[], updates: BatchUpdatePayload[], normalizedFields: Record<string, any>): TRow[] => {
      // Build lookup set for matching
      const updateKeys = new Set(
        updates.map(u => (u.PK && u.SK ? `${u.PK}::${u.SK}` : null)).filter(Boolean)
      )

      return rows.map(row => {
        const rowKey = row.PK && row.SK ? `${row.PK}::${row.SK}` : null
        if (rowKey && updateKeys.has(rowKey)) {
          return { ...row, ...normalizedFields }
        }
        // Also check by ID
        const rowId = config.getRowId(row)
        for (const update of updates) {
          if (update.PK === rowId || (config.cache.secondaryIdField && row[config.cache.secondaryIdField] === update.PK)) {
            return { ...row, ...normalizedFields }
          }
        }
        return row
      })
    },
    [config]
  )

  return {
    updateGridRowsDirectly,
    updateCache,
    invalidateCache,
    applyUpdatesToRows,
  }
}

export default useCacheSync
